import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, PenLine, FileCheck2, GraduationCap, Clock, MessageCircle, PoundSterling, RefreshCcw, Lock } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { Navbar } from "@/components/landing/Navbar";
import { TRUST_STATS, WHATSAPP_URL } from "@/components/landing/data";

const REASONS = [
  { icon: PenLine, title: "100% Human Writers", desc: "Every project is researched and written by a qualified academic — never generated by AI. We can provide an AI-detection report on request." },
  { icon: FileCheck2, title: "Proof of Originality", desc: "Each order is screened with premium plagiarism tools before delivery, and you receive a free originality report with your work." },
  { icon: GraduationCap, title: "Subject-Matched Experts", desc: "Your brief is handled by a writer holding a degree in your exact field — nursing, law, engineering, finance and 100+ more." },
  { icon: Clock, title: "On Time, Every Time", desc: "We plan backwards from your deadline and deliver early wherever possible — including 24-hour urgent orders." },
  { icon: MessageCircle, title: "24/7 Human Support", desc: "Real people on WhatsApp, email and phone — average first response under 15 minutes, day and night." },
  { icon: PoundSterling, title: "Transparent Pricing", desc: "Fixed quotes from £7.50 per 250 words. No hidden fees, no surprises — the price we quote is the price you pay." },
  { icon: RefreshCcw, title: "Free Unlimited Revisions", desc: "14 days of free revisions on every order (30 days for dissertations) until the work matches your brief exactly." },
  { icon: Lock, title: "Total Confidentiality", desc: "Your identity, documents and order details are never shared with anyone. Your privacy is contractually protected." },
];

export const WhyChooseUs = () => (
  <div className="min-h-screen bg-[#FAF8F2]" data-testid="why-choose-us-page">
      <Navbar />

    <main className="mx-auto max-w-7xl px-5 pb-16 pt-[132px] sm:px-8 sm:pb-24">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
        <p className="flex items-center gap-3 font-mono-accent text-[11px] uppercase tracking-[0.35em] text-[#A07E1F]">
          <span className="inline-block h-2 w-2 rotate-45 bg-[#D4AF37]" />
          Why Choose Us
        </p>
        <h1 className="mt-5 font-serif-display text-4xl font-medium leading-tight tracking-tight text-[#12294D] sm:text-6xl" data-testid="wcu-heading">
          Eight reasons students <span className="italic text-[#A07E1F]">trust us</span> with their grades.
        </h1>
        <p className="mt-6 text-base leading-relaxed text-[#46586F] sm:text-lg">
          Anyone can promise quality. We prove it — with originality reports, degree-holding writers,
          transparent pricing and a revision promise that puts the risk on us, not you.
        </p>
      </motion.div>

      <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {REASONS.map((r, i) => (
          <motion.div
            key={r.title}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
            className="luxury-card rounded-xl p-7"
            data-testid={`wcu-reason-${i}`}
          >
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg border border-[#A07E1F]/30 bg-[#D4AF37]/10">
              <r.icon size={20} className="text-[#A07E1F]" />
            </div>
            <h3 className="font-serif-display text-xl font-semibold text-[#12294D]">{r.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#46586F]">{r.desc}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="mt-20 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-[#A07E1F]/20 bg-[#A07E1F]/15 sm:grid-cols-3 lg:grid-cols-6"
        data-testid="wcu-stats"
      >
        {TRUST_STATS.map((s) => (
          <div key={s.label} className="flex flex-col items-center bg-white px-4 py-9 text-center">
            <p className="font-serif-display text-3xl font-semibold text-[#A07E1F]">{s.value}</p>
            <p className="mt-2 font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#5E6E85]">{s.label}</p>
          </div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="luxury-card mt-20 flex flex-col items-start gap-6 rounded-xl p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10"
        data-testid="wcu-cta"
      >
        <div>
          <h2 className="font-serif-display text-2xl font-semibold text-[#12294D] sm:text-3xl">Ready to see the difference?</h2>
          <p className="mt-2 text-sm text-[#46586F]">Get a free, no-obligation quote in under 15 minutes.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a href="/#quote" data-testid="wcu-quote-button" className="group flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 py-3.5 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#12294D] transition-colors hover:bg-[#C9A227]">
            Get a Free Quote <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="wcu-whatsapp-button" className="flex items-center gap-2 rounded-full border border-[#A07E1F]/50 px-7 py-3.5 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/15">
            <MessageCircle size={14} /> WhatsApp Us
          </a>
        </div>
      </motion.div>
    </main>
  </div>
);
